import {create} from 'zustand'
import {toast} from 'react-toastify'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {Button, Dialog} from '@sdox/ui'
import {useBlockMutations} from '@sdox/api'

import type {XcNwBlockFragment} from '@sdox/api'

interface DeleteBlockStoreState {
  data?: {
    blockId: XcNwBlockFragment['id']
    type: XcNwBlockFragment['__typename']
  }
  openDialog: (data: DeleteBlockStoreState['data']) => void
  closeDialog: () => void
}

type DeleteBlockDialogProps = {
  documentVersionId: string
}

export const useDeleteBlockStore = create<DeleteBlockStoreState>((set) => ({
  data: undefined,
  openDialog: (data) => set({data}),
  closeDialog: () => set({data: undefined}),
}))

export const DeleteBlockDialog = (props: DeleteBlockDialogProps) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const {t} = useTranslation()
  const {deleteBlock} = useBlockMutations(props.documentVersionId)
  const data = useDeleteBlockStore(state => state.data)
  const closeDialog = useDeleteBlockStore(state => state.closeDialog)
  const isDialogOpen = data !== undefined

  const onClickDelete = async (): Promise<void> => {
    if (data === undefined) return

    try {
      setIsDeleting(true)
      await deleteBlock(data.blockId)
      toast.success('Block deleted')
      closeDialog()
    } catch (e) {
      console.error(e)
      toast.error('Could not delete block. Please try again later.')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Dialog
      title={`Block löschen`}
      open={isDialogOpen}
      onClose={closeDialog}
      persistent={isDeleting}
    >
      <Dialog.Content>
        <Dialog.Section>
          <div className={'mb-spacing-20'}>
            <p className={'mb-spacing-12 text-p1'}>
              Möchten Sie den Block vom Typ <strong>"{data?.type}"</strong> wirklich löschen?
            </p>
            <p className={'text-p1'}>
              Dieser Schritt kann nicht Rückgängig gemacht werden.
            </p>
          </div>
          <div className={'flex justify-end gap-spacing-8'}>
            <Button
              size={'medium'}
              variant={'flat'}
              onClick={closeDialog}
            >
              {t('common:cancel')}
            </Button>
            <Button
              size={'medium'}
              onClick={onClickDelete}
              loading={isDeleting}
            >
              {t('common:delete')}
            </Button>
          </div>
        </Dialog.Section>
      </Dialog.Content>
    </Dialog>
  )
}
